import { useEffect, useState, useCallback } from 'react'
import { Avatar, Popover, Button } from 'antd'
import {
  UserOutlined,
  LogoutOutlined,
  SettingOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'

import MenuSearch from '../MenuSearch'
import paths from '../../paths'

import './MenuDrawer.less'

// Hook personalizado para responsividade
const useResponsive = () => {
  const [screenSize, setScreenSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0
  })

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight
      })
    }

    // Throttle para otimizar performance
    let timeoutId = null
    const throttledResize = () => {
      if (timeoutId) return

      timeoutId = setTimeout(() => {
        handleResize()
        timeoutId = null
      }, 100)
    }

    window.addEventListener('resize', throttledResize)

    // Cleanup
    return () => {
      window.removeEventListener('resize', throttledResize)
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [])

  return screenSize
}

// Constantes para melhor manutenibilidade
const BREAKPOINT_MOBILE = 500
const STORAGE_KEYS = {
  USUARIO_NOME: 'usuario_nome'
}

const COLORS = {
  PRIMARY: '#ec1a23',
  WHITE: '#fff',
  ERROR: '#ff4d4f'
}

// Componente do menu do usuário (fora do render)
const UserMenuContent = ({ onLogout, onAccessControl }) => (
  <div style={{ minWidth: '200px' }}>
    <Button
      type="text"
      icon={<LogoutOutlined />}
      onClick={onLogout}
      style={{
        width: '100%',
        textAlign: 'left',
        marginBottom: '4px'
      }}
    >
      Sair
    </Button>
    <Button
      type="text"
      icon={<SettingOutlined />}
      onClick={onAccessControl}
      style={{
        width: '100%',
        textAlign: 'left'
      }}
    >
      Visualizar Controle de Acesso
    </Button>
  </div>
)

// Componente da seção do usuário (fora do render)
const UserSection = ({ usuarioNome, isMobile, onLogout, onAccessControl }) => {
  if (!usuarioNome || isMobile) return null

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        marginLeft: 'auto',
        gap: '8px'
      }}
    >
      <span
        style={{
          fontWeight: 'bold',
          fontSize: '14px',
          maxWidth: '150px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {usuarioNome}
      </span>

      <Popover
        content={
          <UserMenuContent
            onLogout={onLogout}
            onAccessControl={onAccessControl}
          />
        }
        placement="bottomRight"
        trigger="click"
        overlayClassName="user-menu-popover"
      >
        <Avatar
          icon={<UserOutlined />}
          style={{
            backgroundColor: COLORS.WHITE,
            color: COLORS.ERROR,
            cursor: 'pointer',
            flexShrink: 0
          }}
          size="default"
        />
      </Popover>
    </div>
  )
}

// PropTypes para validação
UserMenuContent.propTypes = {
  onLogout: PropTypes.func.isRequired,
  onAccessControl: PropTypes.func.isRequired
}

UserSection.propTypes = {
  usuarioNome: PropTypes.string,
  isMobile: PropTypes.bool.isRequired,
  onLogout: PropTypes.func.isRequired,
  onAccessControl: PropTypes.func.isRequired
}

UserSection.defaultProps = {
  usuarioNome: ''
}

const MenuDrawer = () => {
  const [usuarioNome, setUsuarioNome] = useState('')
  const navigate = useNavigate()

  // Hook customizado para responsividade
  const { width: screenWidth } = useResponsive()
  const isMobile = screenWidth < BREAKPOINT_MOBILE

  // Carrega dados do usuário
  useEffect(() => {
    const nome = localStorage.getItem(STORAGE_KEYS.USUARIO_NOME)
    if (nome) {
      setUsuarioNome(nome)
    }
  }, [])

  // Handlers otimizados com useCallback
  const handleLogout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.USUARIO_NOME)
    navigate('/logout')
  }, [navigate])

  const handleAccessControl = useCallback(() => {
    navigate('/menu-usuario/controle-acesso')
  }, [navigate])

  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 16px',
        backgroundColor: COLORS.PRIMARY,
        color: COLORS.WHITE,
        height: '64px',
        minHeight: '64px'
      }}
    >
      <MenuSearch
        menuData={paths}
        style={{
          color: COLORS.WHITE,
          lineHeight: '32px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          flex: isMobile ? 1 : 'none'
        }}
      />

      <UserSection
        usuarioNome={usuarioNome}
        isMobile={isMobile}
        onLogout={handleLogout}
        onAccessControl={handleAccessControl}
      />
    </header>
  )
}

export default MenuDrawer
